import { ChevronRight, MapPin, MessageSquareText, Wine as WineIcon } from "lucide-react";
import { withInventoryReturn } from "../lib/inventory-navigation";
import { Link } from "../lib/router";
import type { Wine } from "../types";
import {
  activeCoravins,
  bottleLabel,
  currentYear,
  getTastingStatus,
  normalizedKey,
  vintageLabel,
  wineLocationStocks,
} from "../lib/wine";
import { TastingBadge } from "./TastingBadge";

export function WineCard({
  wine,
  filteredStock,
  filteredCoravinCount,
  inventoryReturnTo,
}: {
  wine: Wine;
  filteredStock?: number;
  filteredCoravinCount?: number;
  inventoryReturnTo?: string;
}) {
  const status = getTastingStatus(wine);
  const year = currentYear();
  const stockedLocations = wineLocationStocks(wine).filter(
    (item) => item.stock > 0,
  );
  const locationLabel =
    stockedLocations.length > 1
      ? `Réparti sur ${stockedLocations.length} emplacements`
      : stockedLocations[0]?.emplacement ||
        stockedLocations[0]?.emplacement_nom ||
        stockedLocations[0]?.lieu ||
        wine.emplacement ||
        "Emplacement à préciser";
  const displayedStock = filteredStock ?? wine.stock;
  const coravinCount = filteredCoravinCount ?? activeCoravins(wine).length;
  const primaryOrigin = wine.region || wine.appellation || "Origine à préciser";
  const distinctAppellation =
    wine.region &&
    wine.appellation &&
    normalizedKey(wine.region) !== normalizedKey(wine.appellation)
      ? wine.appellation
      : null;
  const originTitle = [primaryOrigin, distinctAppellation]
    .filter((item): item is string => Boolean(item))
    .join(" · ");
  const showCategoryLabel = !["Rouge", "Blanc"].includes(wine.categorie);
  const categoryClass = wine.categorie
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return (
    <article className="wine-card">
      <Link
        to={withInventoryReturn(`/vins/${wine.id}`, inventoryReturnTo ?? "/")}
        aria-label={`Voir ${primaryOrigin}${distinctAppellation ? `, ${distinctAppellation}` : ""}, ${vintageLabel(wine)}, ${wine.producteur}, ${wine.nom}, catégorie ${wine.categorie}, statut de dégustation ${status}, ${filteredStock == null ? bottleLabel(wine.stock) : `${bottleLabel(filteredStock)} ici, ${bottleLabel(wine.stock)} au total`}${coravinCount > 0 ? `, dont ${coravinCount} coravinée${coravinCount > 1 ? "s" : ""}${filteredStock != null ? " ici" : ""}` : ""}, ${locationLabel}`}
      >
        <div className={`wine-accent category-${categoryClass}`} aria-hidden="true">
          <WineIcon size={20} strokeWidth={1.7} />
        </div>
        <div className="wine-card-content">
          <h2>
            {originTitle}{"\u00a0"}
            <span className="wine-vintage">{vintageLabel(wine)}</span>
          </h2>
          <div className="wine-card-body">
            <div className="wine-card-main">
              <div className="wine-card-identity">
                <p className="wine-producer">{wine.producteur}</p>
                <h3 className="wine-cuvee">{wine.nom}</h3>
              </div>
              {showCategoryLabel || status !== "Non renseigné" || wine.a_une_note_degustation || coravinCount > 0 ? (
                <div className="wine-card-tags">
                  {showCategoryLabel ? <span className="category-label">{wine.categorie}</span> : null}
                  {coravinCount > 0 ? <span className="coravin-badge">{coravinCount} coravinée{coravinCount > 1 ? "s" : ""}{filteredStock != null ? " ici" : ""}</span> : null}
                  {status !== "Non renseigné" ? (
                    <TastingBadge
                      status={status}
                      deadlineThisYear={wine.fin_deg === year}
                      deadlinePassed={wine.fin_deg != null && wine.fin_deg < year}
                    />
                  ) : null}
                  {wine.a_une_note_degustation ? (
                    <span className="note-indicator" title="Une note accompagne ce vin">
                      <MessageSquareText size={14} aria-hidden="true" />
                      <span className="sr-only">Ce vin possède une note</span>
                    </span>
                  ) : null}
                </div>
              ) : null}
              <div className="wine-card-location">
                <MapPin size={15} aria-hidden="true" />
                <span>{locationLabel}</span>
              </div>
            </div>
            <div className={`stock-pill ${wine.stock === 0 ? "stock-empty" : ""}`}>
              <span className="stock-pill-primary">
                <strong>{displayedStock}</strong>
                {filteredStock == null ? null : <span>ici</span>}
              </span>
              <span className="stock-pill-secondary">
                {filteredStock == null
                  ? wine.stock === 1
                    ? "bouteille"
                    : "bouteilles"
                  : `${wine.stock} au total`}
              </span>
              <span className="sr-only">{filteredStock == null ? `${bottleLabel(wine.stock)} en stock` : `${bottleLabel(filteredStock)} ici, ${bottleLabel(wine.stock)} au total`}</span>
            </div>
          </div>
        </div>
        <ChevronRight className="wine-chevron" size={19} aria-hidden="true" />
      </Link>
    </article>
  );
}
